import { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';

import { useChatClient, useUserBanListener } from '@bizzabo/chat-core';
import { Text, NewPrivateChat, IconButton, Theme } from '@bizzabo/design-system';
import { useCurrentUser } from '@bizzabo/entities';

import PanelSearch from '../../../../components/PanelSearch';
import { useMessages, View } from '../../../contexts/MessagesContext';
import useTypeTracking from '../../../../../analytics';

const SEARCH_PLACEHOLDER_TEXT = 'Search contacts';

const ChatHeader: React.FC = () => {
    const classes = useStyles();
    const { eventId, isEmptyState, setView, view, setSearchText } = useMessages();
    const { client } = useChatClient();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const { trackClickEvent, trackSystemEvent } = useTypeTracking();
    const { data: currentUser } = useCurrentUser(eventId);
    const isInitialEmptyView = useRef(true);
    const isMyUserBanned = useUserBanListener(client);
    const isFirstType = useRef(true);

    useEffect(() => {
        if (isSearchOpen && view === View.Channel) {
            setIsSearchOpen(false);
        }
    }, [view]);

    useEffect(() => {
        if (isEmptyState && isInitialEmptyView?.current) {
            setIsSearchOpen(isEmptyState);
        }
    }, [isEmptyState]);

    const openSearchState = (view: number) => {
        setIsSearchOpen(true);
        setView(view);
        trackClickEvent({
            subCategory: 'CREATE_NEW_CHANNEL',
        });
    };

    const closeSearchState = (view: number) => {
        setIsSearchOpen(false);
        isInitialEmptyView.current = false;
        setView(view);
    };

    const onSearchHandle = (searchText: string) => {
        setSearchText(searchText);
        if (isFirstType.current) {
            trackSystemEvent({
                description: 'SEARCH_CONTACTS_START',
            });
            isFirstType.current = false;
        }
    };

    return (
        <div className={classes.chatHeader}>
            {isSearchOpen ? (
                <PanelSearch
                    onChange={onSearchHandle}
                    closeSearch={() => closeSearchState(View.ChannelList)}
                    placeholder={SEARCH_PLACEHOLDER_TEXT}
                />
            ) : (
                <div className={classes.firstRow}>
                    <Text variant="t40" className={classes.title}>
                        Messages
                    </Text>
                    <div className={classes.actions}>
                        <IconButton
                            icon={NewPrivateChat}
                            className={classes.panelBtn}
                            onClick={() => openSearchState(View.Search)}
                            title="new conversation button"
                            disabled={currentUser.isHidden || isMyUserBanned}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const useStyles = makeStyles<Theme>(
    theme => ({
        chatHeader: {
            display: 'flex',
            alignItems: 'center',
            backgroundColor: theme.palette.greys[100],
            borderRadius: '33px 33px 0 0',
            minHeight: 62,
            padding: '12px 24px 0',

            [theme.breakpoints.up('sm')]: {
                borderRadius: '33px 0 0 0',
                minHeight: 92,
                padding: '0 24px',
            },
        },
        firstRow: {
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        title: {
            minWidth: 53,
            color: theme.customization.interactive.G30,
        },
        actions: {
            display: 'flex',
            alignItems: 'center',
        },
        panelBtn: {
            width: 42,
            height: 42,
            background: 'none',
            color: theme.customization.interactive.C10,
            '&:hover': {
                background: 'none',
                color: theme.customization.interactive.C20,
            },
            '&:disabled': {
                background: 'none',
                opacity: 0.5,
            },
        },
    }),
    { classNamePrefix: ChatHeader.name }
);
export default ChatHeader;
